import React, { useEffect, useState } from "react";
import { Dialog, Text, TextField, ButtonVariant } from "@merge-api/merge-javascript-shared";

interface Props {
  isShown: boolean;
  onHide: () => void;
  title: string;
  description: string;
  primaryButtonText: string;
  primaryButtonVariant: ButtonVariant.PrimaryGray | ButtonVariant.DangerFilled;
  onSubmit: (currentTotpToken: string, onDone: (errorText?: string) => void) => void;
}

const CurrentTwoFACodeModal = ({
  isShown,
  onHide,
  title,
  description,
  primaryButtonText,
  primaryButtonVariant,
  onSubmit,
}: Props) => {
  // state
  const [currentTotpToken, setCurrentTotpToken] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string | undefined>();

  // effects
  // Clears the previous attempt's code and error each time the dialog opens
  useEffect(() => {
    if (isShown) {
      setCurrentTotpToken("");
      setIsSubmitting(false);
      setErrorText(undefined);
    }
  }, [isShown]);

  // event handlers
  const onChangeCode = (value: string) => {
    setCurrentTotpToken(value.replace(/\D/g, "").slice(0, 6));
    setErrorText(undefined);
  };

  const onClickSubmit = () => {
    if (currentTotpToken.length !== 6 || isSubmitting) return;
    setIsSubmitting(true);
    setErrorText(undefined);
    onSubmit(currentTotpToken, (submitErrorText) => {
      setIsSubmitting(false);
      if (submitErrorText) {
        setErrorText(submitErrorText);
      } else {
        onHide();
      }
    });
  };

  // render
  return (
    <Dialog
      open={isShown}
      onClose={onHide}
      variant="md"
      title={title}
      primaryButtonText={primaryButtonText}
      primaryButtonVariant={primaryButtonVariant}
      primaryButtonLoading={isSubmitting}
      primaryButtonDisabled={currentTotpToken.length !== 6 || isSubmitting}
      onPrimaryButtonClick={onClickSubmit}
      secondaryButtonText="Cancel"
      secondaryButtonVariant={ButtonVariant.TextBlack}
      onSecondaryButtonClick={onHide}
    >
      <div className="flex flex-col gap-8">
        <Text>{description}</Text>
        <TextField
          labelTitle="Current 6-digit verification code"
          placeholder="Enter code"
          value={currentTotpToken}
          onChange={(e) => onChangeCode(e.target.value)}
          inputMode="numeric"
          autoComplete="one-time-code"
          error={!!errorText}
          errorText={errorText}
        />
      </div>
    </Dialog>
  );
};

export default CurrentTwoFACodeModal;
